import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, Stack, SxProps } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { formatTime, getRecruitingSlaDuration, getTaskStatusByDuration } from './utils';
import { useDateService } from 'framework/utils';
import { useContextMenuContext } from '../contextMenu/ContextMenuContext';
import CandidateCard from './CanditateCard';
import { Draggable } from 'framework/dnd';
import useRecruitingBoardService from '../../useRecruitingBoardService';
import { TaskStyles } from '../../types';
import { MINUTE } from '../../constants';

interface DraggableItemProps {
  item: any;
  columnSettings: any;
  cardSx?: SxProps;
  isClosedOrRejected?: boolean;
}

const getCardStyles = (deadlineStyle: TaskStyles, isSlaHidden = false) => ({
  borderRadius: 1,
  borderStyle: 'solid',
  borderWidth: 1,
  borderColor: isSlaHidden ? 'border.contrast' : deadlineStyle.borderColor,
  minWidth: '195px',
});

const DragHandle = React.forwardRef<HTMLDivElement, { background: string }>(({ background }, ref) => (
  <Box
    ref={ref}
    sx={{
      minHeight: '100%',
      width: '10px',
      backgroundColor: background,
      cursor: 'grab',
    }}
  >
    <DragIndicatorIcon sx={{ width: '10px', height: '100%', color: 'common.white' }} fontSize="small" />
  </Box>
));

const DraggableItem: React.FC<DraggableItemProps> = ({ item, columnSettings, cardSx, isClosedOrRejected }) => {
  const { getCurrentDateTime } = useDateService();
  const [timeElapsed, setTimeElapsed] = useState(getCurrentDateTime());
  const { performJobTransition } = useRecruitingBoardService();
  const { handleContextMenu = () => {} } = useContextMenuContext();
  const isSlaHidden = isClosedOrRejected || !columnSettings?.deadlinesForStage;

  useEffect(() => {
    if (!isSlaHidden) {
      const intervalId = setInterval(() => setTimeElapsed(getCurrentDateTime()), MINUTE);
      return () => clearInterval(intervalId);
    }
  }, [getCurrentDateTime, isSlaHidden]);

  const timeDifference = useMemo(
    () => (isSlaHidden ? 0 : getRecruitingSlaDuration(item.eventTimestamp, timeElapsed)),
    [isSlaHidden, item.eventTimestamp, timeElapsed],
  );

  const deadlineStyle = useMemo(
    () => getTaskStatusByDuration(timeDifference, columnSettings?.deadlinesForStage),
    [timeDifference, columnSettings?.deadlinesForStage],
  );

  const formattedTime = useMemo(() => formatTime(timeDifference), [timeDifference]);

  const renderPreview = useCallback(
    () => (
      <Card sx={getCardStyles(deadlineStyle, isSlaHidden)}>
        <Stack direction="row" alignItems="stretch">
          <DragHandle background={isSlaHidden ? 'text.secondary' : deadlineStyle.dragIconBackground} />
          <CandidateCard
            item={item}
            formattedTime={formattedTime}
            deadlineStyle={deadlineStyle}
            preview
            hideSla={isSlaHidden}
          />
        </Stack>
      </Card>
    ),
    [deadlineStyle, formattedTime, isSlaHidden, item],
  );

  return (
    <Draggable canDrag={item.canDrag} isHandleRef preview={renderPreview()} initialData={item}>
      {({ draggableRef }) => (
        <div onContextMenu={e => handleContextMenu(e, item)} style={{ cursor: 'context-menu' }}>
          <Card sx={{ ...getCardStyles(deadlineStyle, isSlaHidden), opacity: item.canDrag ? 1 : 0.5, ...cardSx }}>
            <Stack direction="row" alignItems="stretch" minHeight="36px">
              <DragHandle
                ref={draggableRef}
                background={isSlaHidden ? 'text.secondary' : deadlineStyle.dragIconBackground}
              />
              <CandidateCard
                performJobTransition={performJobTransition}
                item={item}
                formattedTime={formattedTime}
                deadlineStyle={deadlineStyle}
                hideSla={isSlaHidden}
              />
            </Stack>
          </Card>
        </div>
      )}
    </Draggable>
  );
};

export default React.memo(DraggableItem);
